import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Home.css';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import { CONFIG } from '../config';
import { generateLocalBusinessSchema, generateBreadcrumbSchema } from '../utils/seoHelpers';
import TrustSignals from '../components/TrustSignals';
import FAQSection from '../components/FAQSection';
import CTASection from '../components/CTASection';

import interlockImg from '../assets/Interlock/interlock_01.webp';
import landscapingImg from '../assets/Landscaping/landscaping_01.webp';
import decksImg from '../assets/Decks_&_Railings/deck_01.webp';
import wallImg from '../assets/Retaining_Walls/retaining_01.webp';
import pergolaImg from '../assets/Pergolas/pergola_01.webp';
import repairImg from '../assets/Relevels_&_Repairs/relevel_01.webp';
import drivewayImg from '../assets/Driveways/driveway_02.webp';
import fenceImg from '../assets/Fences/fence_01.webp';
import pressureImg from '../assets/Pressure_Washing_&_Resand/pressure_01.webp';
import firepitImg from '../assets/Fire_Pits/fire_01.webp';
import designImg from '../assets/3D_Design/3d_design_01.webp';
import showroomImg from '../assets/Showroom/Finished_Showroom.webp';

const imagesToPreload = [
  interlockImg,
  landscapingImg,
  decksImg,
  wallImg,
  pergolaImg,
  repairImg,
  drivewayImg,
  fenceImg,
  pressureImg,
  firepitImg,
  designImg,
  showroomImg
];

imagesToPreload.forEach(src => {
  const img = new window.Image();
  img.src = src;
});

const services = [
  {
    name: 'showroom',
    translationKey: 'showroom',
    img: showroomImg,
    descKey: 'showroomDesc',
  },
  {
    name: 'interlock',
    translationKey: 'interlock',
    img: interlockImg,
    descKey: 'interlockDesc',
  },
  {
    name: 'retainingWalls',
    translationKey: 'retainingWalls',
    img: wallImg,
    descKey: 'retainingWallsDesc',
  },
  {
    name: 'landscaping',
    translationKey: 'landscaping',
    img: landscapingImg,
    descKey: 'landscapingDesc',
  },
  {
    name: 'driveways',
    translationKey: 'driveways',
    img: drivewayImg,
    descKey: 'drivewaysDesc',
  },
  {
    name: 'fences',
    translationKey: 'fences',
    img: fenceImg,
    descKey: 'fencesDesc',
  },
  {
    name: 'decks',
    translationKey: 'decking',
    img: decksImg,
    descKey: 'decksDesc',
  },
  {
    name: 'firePits',
    translationKey: 'firePits',
    img: firepitImg,
    descKey: 'firePixtsDesc',
  },
  {
    name: 'design',
    translationKey: 'design',
    img: designImg,
    descKey: 'designDesc',
  },
  {
    name: 'pressureWashing',
    translationKey: 'pressureWashing',
    img: pressureImg,
    descKey: 'pressureDesc',
  },
  {
    name: 'pergolas',
    translationKey: 'pergolas',
    img: pergolaImg,
    descKey: 'pergolaDesc',
  },
  {
    name: 'relevel',
    translationKey: 'relevel',
    img: repairImg,
    descKey: 'relevelDesc',
  }
];

const slug = (name) => name.replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();

const getServiceRoute = (name) => {
  const interlockServices = ['interlock', 'retainingWalls', 'driveways', 'firePits', 'pressureWashing', 'relevel'];
  if (interlockServices.includes(name)) return '/interlock';
  if (name === 'landscaping' || name === 'pergolas') return '/landscaping';
  if (name === 'decks') return '/decks';
  if (name === 'fences') return '/fences';
  if (name === 'showroom') return '/projects#showroom';
  if (name === 'design') return '/projects#3d-design';
  return `/projects#${slug(name)}`; // fallback for other service anchors
};

const Home = () => {
  const { language } = useLanguage();
  const t = (key) => translations[language]?.[key] || key;

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: CONFIG.urls.home }
  ]);

  return (
    <div className="home">
      <Helmet>
        <title>{t('homeTitle')}</title>
        <meta name="description" content={t('metaDescription')} />
        <meta name="keywords" content="landscaping Ottawa, interlock patio, decks, fences, landscaping services, retaining walls" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={CONFIG.urls.home} />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CONFIG.urls.home} />
        <meta property="og:title" content={t('homeTitle')} />
        <meta property="og:description" content={t('metaDescription')} />
        <meta property="og:image" content="https://www.3brothersottawalandscaping.ca/og-image.png" />
        <meta property="og:site_name" content="3 Brothers Ottawa Landscaping" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('homeTitle')} />
        <meta name="twitter:description" content={t('metaDescription')} />
        <meta name="twitter:image" content="https://www.3brothersottawalandscaping.ca/og-image.png" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(generateLocalBusinessSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <section
        className="hero"
        style={{
          backgroundImage: `url(${interlockImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="hero-content">
          <h1>{t('heroTitle')}</h1>
          <Link to="/contact-us"><button className="hero-button">{t('getQuote')}</button></Link>
        </div>
      </section>

      <section className="services-section">
        <div className="home-content">
          <h1>{t('ourServices')}</h1>
          <div className="services-list">
            {services.map(service => (
              <Link key={service.name} to={getServiceRoute(service.name)} className="service-link">
                <div className="service-card large" >
                  <img src={service.img} alt={`${t(service.translationKey)} - 3 Brothers Ottawa Landscaping`} />
                  <div>
                    <h3>{t(service.translationKey)}</h3>
                    <p>{t(service.descKey)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <TrustSignals />

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <CTASection 
        title={t('readyToStart')}
        subtitle={t('projectInMind')}
        showTrustBadges={true}
      />
    </div>
  );
};

export default Home;